import React, { useState, useEffect, useMemo } from "react";
import { api } from "../../api";
import { Icon, useToast } from "../../components/ui";
import { derive } from "./recordDerive";

const SHEET_LABEL = { SUBMISSION: "Submission", END_PROCESS: "End Process" };

function pacificToday() {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "2-digit", day: "2-digit", year: "numeric" }).format(new Date());
  } catch { const d = new Date(); return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`; }
}

// MM/DD/YYYY  ->  YYYY-MM-DD  (for <input type="date">)
function toISO(mdy) {
  if (!mdy) return "";
  const m = String(mdy).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
}
// YYYY-MM-DD  ->  MM/DD/YYYY  (store in the app's format)
function fromISO(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[2]}/${m[3]}/${m[1]}`;
}

export function AddRecordView({ sheet, assignees, editRecord, allowedGroups, onClose, onCreated }) {
  const toast = useToast();
  const isEdit = !!editRecord;
  const ed = isEdit ? derive(editRecord) : null;
  const [ref, setRef] = useState(null);
  const [orderList, setOrderList] = useState([]);
  // A group-scoped admin can only file into the group given to them — default (and lock) to it.
  const lockedGroup = (allowedGroups && allowedGroups.length === 1) ? allowedGroups[0] : "";
  const [group, setGroup] = useState(isEdit ? (editRecord.productionGroup || ed.location || "") : lockedGroup);
  const [mrn, setMrn] = useState(isEdit ? (editRecord.mrn || "") : "");
  const [order, setOrder] = useState(isEdit ? (editRecord.orderName || ed.orders || "") : "");
  const [location, setLocation] = useState(isEdit ? (ed.clinic || "") : "");
  const [payor, setPayor] = useState(isEdit ? (ed.insurance || "") : "");
  const [orderMD, setOrderMD] = useState(isEdit ? (ed.orderMD || "") : "");
  const [rtcDate, setRtcDate] = useState(isEdit ? (ed.rtcDate || "") : "");
  const [urgency, setUrgency] = useState(isEdit ? (ed.urgentRoutine === "Urgent" ? "Urgent" : "Routine") : "Urgent");
  const [orderDate, setOrderDate] = useState(isEdit ? (editRecord.orderDate || ed.orderDate || pacificToday()) : pacificToday());
  const [assignTo, setAssignTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.getRefData().then(setRef).catch(() => setRef({})); }, []);
  useEffect(() => { api.orderNames().then((n) => setOrderList(n || [])).catch(() => setOrderList([])); }, []);

  const groups = useMemo(() => {
    if (allowedGroups && allowedGroups.length) return allowedGroups;   // admin restricted to their assigned group(s)
    const g = new Set(["LACN", "CCCMG", "Surgical", "Auth Not Needed", "PHS"]);
    (ref?.locations || []).forEach((l) => l.userGroup && g.add(l.userGroup));
    return [...g];
  }, [ref, allowedGroups]);
  const locations = useMemo(() => {
    let ls = ref?.locations || [];
    if (group) ls = ls.filter((l) => (l.userGroup || "") === group);
    return Array.from(new Set(ls.map((l) => l.sourceName).filter(Boolean)));
  }, [ref, group]);
  const payorPortal = ref?.payorPortal || {};
  const payors = useMemo(() => Object.keys(payorPortal), [payorPortal]);
  const orders = orderList;
  const portal = useMemo(() => {
    if (!payor) return "";
    const key = payor.trim().toLowerCase();
    const hit = Object.keys(payorPortal).find((k) => k.trim().toLowerCase() === key);
    return hit ? payorPortal[hit] : "";
  }, [payor, payorPortal]);

  const submit = async () => {
    setErr("");
    if (!group) return setErr("Choose a group.");
    if (!mrn.trim()) return setErr("PID (MRN) is required.");
    if (!order.trim()) return setErr("Order is required.");
    setBusy(true);
    try {
      const body = {
        sheet, group, mrn: mrn.trim(), orderName: order.trim(),
        location: location.trim() || null, payor: payor.trim() || null,
        orderMD: orderMD.trim() || null, rtcDate: rtcDate.trim() || null, urgency, orderDate: orderDate.trim() || null,
        assignToUserId: assignTo ? Number(assignTo) : null,
      };
      if (isEdit) { const rec = await api.updateManualRecord(editRecord.id, body); toast("Record updated", "ok"); onCreated(rec); }
      else { const rec = await api.createManualRecord(body); toast("Record added", "ok"); onCreated(rec); }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="addrec-wrap">
      <div className="addrec-card">
        <div className="addrec-head">
          <div>
            <h2>{isEdit ? "Edit record" : "Add a new record"}</h2>
            <p>{SHEET_LABEL[sheet] || sheet}{isEdit ? " · manually-added record" : ""}</p>
          </div>
          <button className="wv-x" onClick={onClose} title="Close" aria-label="Close"><Icon.x width="18" height="18" /></button>
        </div>

        <div className="addrec-body">
          <div className="addrec-section">Patient &amp; order</div>
          <div className="addrec-grid">
            <div className="field req"><label>Group</label>
              <select className="inp" value={group} onChange={(e) => setGroup(e.target.value)} disabled={!!lockedGroup && !isEdit}>
                {!lockedGroup && <option value="">Select group…</option>}
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select></div>
            <div className="field req"><label>PID (MRN)</label>
              <input className="inp" value={mrn} onChange={(e) => setMrn(e.target.value)} placeholder="Enter patient ID" /></div>
            <div className="field req"><label>Order</label>
              <input className="inp" list="ar-orders" value={order} onChange={(e) => setOrder(e.target.value)} placeholder="Search or type an order" />
              <datalist id="ar-orders">{orders.map((o) => <option key={o} value={o} />)}</datalist></div>
            <div className="field"><label>OrderMD</label>
              <input className="inp" value={orderMD} onChange={(e) => setOrderMD(e.target.value)} placeholder="Ordering physician (optional)" /></div>
          </div>

          <div className="addrec-section">Location &amp; payor</div>
          <div className="addrec-grid">
            <div className="field"><label>Clinic Location</label>
              <input className="inp" list="ar-locs" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Search or type a location" />
              <datalist id="ar-locs">{locations.map((l) => <option key={l} value={l} />)}</datalist></div>
            <div className="field"><label>Payor / IPA</label>
              <input className="inp" list="ar-payors" value={payor} onChange={(e) => setPayor(e.target.value)} placeholder="Search or type a payor" />
              <datalist id="ar-payors">{payors.map((p) => <option key={p} value={p} />)}</datalist></div>
            <div className="field"><label>Portal <span className="hint-inline">(auto from payor)</span></label>
              <input className="inp inp-read" value={portal || "—"} readOnly /></div>
            <div className="field"><label>RTC / appointment date</label>
              <input className="inp" type="date" value={toISO(rtcDate)} onChange={(e) => setRtcDate(fromISO(e.target.value))} /></div>
          </div>

          <div className="addrec-section">Assignment</div>
          <div className="addrec-grid">
            <div className="field"><label>Clinic Priority</label>
              <input className="inp inp-read" value="STAT" readOnly /></div>
            <div className="field"><label>Urgent / Routine</label>
              <select className="inp" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
                <option value="Urgent">Urgent</option>
                <option value="Routine">Routine</option>
              </select></div>
            <div className="field"><label>Order Date <span className="hint-inline">(defaults to today — change if needed)</span></label>
              <input className="inp" type="date" value={toISO(orderDate)} onChange={(e) => setOrderDate(fromISO(e.target.value))} /></div>
            {!isEdit && <div className="field"><label>Assign to</label>
              <select className="inp" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                <option value="">Leave unassigned</option>
                {(assignees || []).map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
              </select></div>}
          </div>

          {err && <div className="err-text">{err}</div>}
        </div>

        <div className="addrec-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? <span className="spin" /> : isEdit ? "Save" : "Add record"}</button>
        </div>
      </div>
    </div>
  );
}